import { useState } from "react";
import { NavLink as RouterLink } from "react-router-dom";
import {
  Assignment,
  ContactPage,
  Delete,
  Edit,
  Settings as ModeEdit,
  History,
  Comment,
  MedicalServices,
  MoreVert,
  NextPlan,
  PauseCircle,
  RemoveRedEye,
  Restore,
  ThumbDown,
  ThumbUp,
  VideoLibrary,
} from "@mui/icons-material";
import {
  IconButton,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  useTheme,
} from "@mui/material";
import type { IconButtonProps } from "@mui/material/IconButton";
import { useTranslation } from "react-i18next";
import {
  StyledListItemIcon,
  StyledListItemText,
} from "../../../styles/listStyled";
import { AuthUser, Role } from "../../auth/types/authType";
import { isPrivilegedTransaction } from "../../../utils/authUtils";
import { Employee, EmployeeStatus } from "../types/processTypes";
import {
  EmployeeStatusTabValue,
  shouldShowDeselectAction,
  shouldShowProcessStagesAction,
  shouldShowViewCvAction,
} from "../utils/employeeStatusTab";

const FULL_EMPLOYEE_EDIT_STATUSES = new Set<EmployeeStatus>([
  EmployeeStatus.pending,
  EmployeeStatus.new,
  EmployeeStatus.selected,
  EmployeeStatus.contracted,
]);

function canShowFullEmployeeEdit(status: EmployeeStatus | undefined): boolean {
  return !!status && FULL_EMPLOYEE_EDIT_STATUSES.has(status);
}

interface EmployeeListRowActionsProps {
  row: Employee;
  activeStatusTab: EmployeeStatusTabValue;
  user: AuthUser | undefined;
  showMedicalAction: boolean;
  hideEmployeeDetailAction?: boolean;
  iconSize?: IconButtonProps["size"];
  onAgentSelect: (employee: Employee) => void;
  onSelectForVisa: (employee: Employee) => void;
  onDeselect: (employee: Employee) => void;
  onChangeStatus: (employee: Employee) => void;
  onOpenMedical: (employee: Employee) => void;
  onRestoreToAvailable: (employee: Employee) => void;
  onOpenStatusHistory: (employee: Employee) => void;
  onOpenRemarks: (employee: Employee) => void;
  onPlayVideo: (employee: Employee) => void;
  onOpenLimitedEdit: (employee: Employee) => void;
  onStopProcess: (employee: Employee) => void;
  onDelete: (employeeId: string) => void;
}

export function EmployeeListRowActions({
  row,
  activeStatusTab,
  user,
  showMedicalAction,
  hideEmployeeDetailAction = false,
  iconSize = "small",
  onAgentSelect,
  onSelectForVisa,
  onDeselect,
  onChangeStatus,
  onOpenMedical,
  onRestoreToAvailable,
  onOpenStatusHistory,
  onOpenRemarks,
  onPlayVideo,
  onOpenLimitedEdit,
  onStopProcess,
  onDelete,
}: EmployeeListRowActionsProps) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { t: te } = useTranslation("enums");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const roles = user?.roles as Role[];
  const status = row.status as EmployeeStatus;

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const showSelectActions =
    (activeStatusTab === "available" || activeStatusTab === "selected") &&
    isPrivilegedTransaction(roles, "ProcessControl.Select");

  const showDeselectAction = shouldShowDeselectAction(
    row,
    roles,
    activeStatusTab,
  );

  const showStatusAction = shouldShowProcessStagesAction(
    row,
    roles,
    activeStatusTab,
  );

  const showRestoreAction =
    (activeStatusTab === "stoppedStatuses" &&
      isPrivilegedTransaction(roles, "ProcessControl.Stop")) ||
    (activeStatusTab === "discontinuedStatuses" &&
      isPrivilegedTransaction(roles, "ProcessControl.Discontinue"));

  const showEmployeeDetailAction =
    !hideEmployeeDetailAction &&
    isPrivilegedTransaction(roles, "Employees.View");

  const showStatusHistoryAction = isPrivilegedTransaction(
    roles,
    "Employees.View",
  );

  const showRemarksAction = isPrivilegedTransaction(roles, "Employees.View");

  const showVideoAction =
    (status === EmployeeStatus.pending ||
      status === EmployeeStatus.new ||
      status === EmployeeStatus.selected) &&
    !!row.employeeVideo;

  const showFullEditAction =
    canShowFullEmployeeEdit(status) &&
    isPrivilegedTransaction(roles, "Records.EditEmployee");

  const showModeEditAction = isPrivilegedTransaction(
    roles,
    "Records.EditEmployee",
  );

  const showStopAction =
    (activeStatusTab === "available" || activeStatusTab === "pendingInfo") &&
    isPrivilegedTransaction(roles, "ProcessControl.Stop");

  const showDeleteAction =
    (status === EmployeeStatus.new || status === EmployeeStatus.pending) &&
    isPrivilegedTransaction(roles, "Records.DeleteEmployee");

  const showViewCvAction = shouldShowViewCvAction(row, roles);

  const hasMenuItems =
    showModeEditAction ||
    showSelectActions ||
    showDeselectAction ||
    showStatusAction ||
    showMedicalAction ||
    showRestoreAction ||
    showStatusHistoryAction ||
    showRemarksAction ||
    showStopAction ||
    showDeleteAction;

  const menuId = `employee-row-menu-${row._id}`;

  return (
    <Stack direction="row" alignItems="center">
      {showEmployeeDetailAction && (
        <Tooltip title="Employee Detail">
          <IconButton
            color="primary"
            component={RouterLink}
            to={`/app/employee-detail/${row._id}`}
            size={iconSize}
          >
            <ContactPage fontSize={iconSize === "small" ? "small" : "medium"} />
          </IconButton>
        </Tooltip>
      )}
      {showViewCvAction && (
        <Tooltip title={t("view") + " " + t("cv")}>
          <IconButton
            color="primary"
            component={RouterLink}
            to={`/app/cvp/${row._id}`}
            size={iconSize}
          >
            <RemoveRedEye
              fontSize={iconSize === "small" ? "small" : "medium"}
            />
          </IconButton>
        </Tooltip>
      )}
      {showFullEditAction && (
        <Tooltip title={t("edit")}>
          <IconButton
            color="primary"
            component={RouterLink}
            to={`/app/employee/${row._id}`}
            size={iconSize}
          >
            <Edit fontSize={iconSize === "small" ? "small" : "medium"} />
          </IconButton>
        </Tooltip>
      )}
      {showVideoAction && (
        <Tooltip title="Play video">
          <IconButton
            color="primary"
            onClick={() => onPlayVideo(row)}
            size={iconSize}
          >
            <VideoLibrary
              fontSize={iconSize === "small" ? "small" : "medium"}
            />
          </IconButton>
        </Tooltip>
      )}
      {hasMenuItems && (
        <>
          <Tooltip title={String(t("actions"))}>
            <span>
              <IconButton
                aria-label="Actions"
                aria-controls={menuId}
                aria-haspopup="true"
                color="secondary"
                onClick={handleMenuOpen}
                size={iconSize}
              >
                <MoreVert
                  fontSize={iconSize === "small" ? "small" : "medium"}
                />
              </IconButton>
            </span>
          </Tooltip>
          <Menu
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: theme.direction === "ltr" ? "right" : "left",
            }}
            id={menuId}
            keepMounted
            transformOrigin={{
              vertical: "top",
              horizontal: theme.direction === "ltr" ? "right" : "left",
            }}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              elevation: 0,
              sx: {
                overflow: "visible",
                filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
                mt: 1,
              },
            }}
          >
            <ListSubheader
              sx={{
                fontSize: "0.75rem",
                fontWeight: 600,
                lineHeight: 1.4,
                color: "text.secondary",
                textTransform: "capitalize",
              }}
            >
              {row.givenName}
            </ListSubheader>

            {showSelectActions && status === EmployeeStatus.new && (
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  onAgentSelect(row);
                }}
              >
                <StyledListItemIcon active={0}>
                  <ThumbUp color="secondary" fontSize="small" />
                </StyledListItemIcon>
                <StyledListItemText active={0}>
                  {t("only_select")}
                </StyledListItemText>
              </MenuItem>
            )}
            {showSelectActions && (
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  onSelectForVisa(row);
                }}
              >
                <StyledListItemIcon active={0}>
                  <Assignment color="secondary" fontSize="small" />
                </StyledListItemIcon>
                <StyledListItemText active={0}>
                  {t("addAgreement")}
                </StyledListItemText>
              </MenuItem>
            )}
            {showDeselectAction && (
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  onDeselect(row);
                }}
              >
                <StyledListItemIcon active={0}>
                  <ThumbDown color="secondary" fontSize="small" />
                </StyledListItemIcon>
                <StyledListItemText active={0}>
                  {t("deselect")}
                </StyledListItemText>
              </MenuItem>
            )}
            {showStatusAction && (
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  onChangeStatus(row);
                }}
              >
                <StyledListItemIcon active={0}>
                  <NextPlan color="secondary" fontSize="small" />
                </StyledListItemIcon>
                <StyledListItemText active={0}>
                  {t("status")}
                </StyledListItemText>
              </MenuItem>
            )}
            {showMedicalAction && (
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  onOpenMedical(row);
                }}
              >
                <StyledListItemIcon active={0}>
                  <MedicalServices color="primary" fontSize="small" />
                </StyledListItemIcon>
                <StyledListItemText active={0}>
                  {te("medical_coc")}
                </StyledListItemText>
              </MenuItem>
            )}
            {showRestoreAction && (
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  onRestoreToAvailable(row);
                }}
              >
                <StyledListItemIcon active={0}>
                  <Restore color="success" fontSize="small" />
                </StyledListItemIcon>
                <StyledListItemText active={0}>
                  {t("restoreToAvailable")}
                </StyledListItemText>
              </MenuItem>
            )}
            {showStatusHistoryAction && (
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  onOpenStatusHistory(row);
                }}
              >
                <StyledListItemIcon active={0}>
                  <History color="secondary" fontSize="small" />
                </StyledListItemIcon>
                <StyledListItemText active={0}>
                  {t("statusHistory")}
                </StyledListItemText>
              </MenuItem>
            )}
            {showRemarksAction && (
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  onOpenRemarks(row);
                }}
              >
                <StyledListItemIcon active={0}>
                  <Comment color="secondary" fontSize="small" />
                </StyledListItemIcon>
                <StyledListItemText active={0}>
                  {t("remarks")}
                </StyledListItemText>
              </MenuItem>
            )}
            {showStopAction && (
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  onStopProcess(row);
                }}
              >
                <StyledListItemIcon active={0}>
                  <PauseCircle color="error" fontSize="small" />
                </StyledListItemIcon>
                <StyledListItemText active={0}>
                  {t("stopProcess")}
                </StyledListItemText>
              </MenuItem>
            )}
            {showModeEditAction && (
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  onOpenLimitedEdit(row);
                }}
              >
                <StyledListItemIcon active={0}>
                  <ModeEdit color="primary" fontSize="small" />
                </StyledListItemIcon>
                <StyledListItemText active={0}>Edit record</StyledListItemText>
              </MenuItem>
            )}
            {showDeleteAction && (
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  onDelete(row._id as string);
                }}
              >
                <StyledListItemIcon active={0}>
                  <Delete color="secondary" fontSize="small" />
                </StyledListItemIcon>
                <StyledListItemText active={0}>
                  {t("delete")}
                </StyledListItemText>
              </MenuItem>
            )}
          </Menu>
        </>
      )}
    </Stack>
  );
}
